import React, { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate, useLocation } from "react-router-dom";
import DashboardLayout from "./DashboardLayout";

function Wallet() {
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false); // shows "Adding credits..."
  const baseCreditsRef = useRef(null); // remember credits at first load
  const processedSubRef = useRef(null); // will hold an unsubscribe fn
  const timersRef = useRef({ soft: null, hard: null }); // store timer ids
  const navigate = useNavigate();
  const location = useLocation();

  // Helper: remove purchase-related params from URL without reload
  const cleanReturnParams = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("success");
    url.searchParams.delete("session_id");
    window.history.replaceState({}, document.title, url.pathname + url.search);
  };

  // Helper: stop processing banner and cleanup
  const finishProcessing = () => {
    setLoading(false);
    if (processedSubRef.current) {
      processedSubRef.current();
      processedSubRef.current = null;
    }
    if (timersRef.current.soft) window.clearTimeout(timersRef.current.soft);
    if (timersRef.current.hard) window.clearTimeout(timersRef.current.hard);
    timersRef.current.soft = null;
    timersRef.current.hard = null;
    cleanReturnParams();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const success = params.get("success") === "true";
    const sessionId = params.get("session_id");

    // If we just returned from Stripe, show "Adding credits..."
    const comingFromCheckout = success || !!sessionId;
    setLoading(comingFromCheckout);

    const user = auth.currentUser;
    if (!user) return;

    const userRef = doc(db, "users", user.uid);

    // Real-time listener for the user's credits
    const unsubUser = onSnapshot(
      userRef,
      (snap) => {
        if (!snap.exists()) return;

        const current = Number(snap.data()?.credits || 0);
        setCredits(current);

        // Capture baseline on first snapshot
        if (baseCreditsRef.current === null) {
          baseCreditsRef.current = current;
        }

        // If we came back from checkout and credits changed, we're done
        if (
          comingFromCheckout &&
          loading &&
          baseCreditsRef.current !== null &&
          current !== baseCreditsRef.current
        ) {
          baseCreditsRef.current = current;
          finishProcessing();
        }
      },
      (err) => {
        console.error("Wallet/onSnapshot error:", err);
      }
    );

    // If we have a session_id, also watch processedSessions/{session_id} as a webhook signal
    if (sessionId) {
      const processedRef = doc(db, "processedSessions", sessionId);
      processedSubRef.current = onSnapshot(
        processedRef,
        (snap) => {
          if (snap.exists()) {
            finishProcessing();
          }
        },
        (err) => {
          console.warn("processedSessions listener error:", err);
        }
      );
    }

    // Safety timers so the UI never feels stuck
    if (comingFromCheckout) {
      // Soft fallback after ~8s: re-check credits once
      timersRef.current.soft = window.setTimeout(async () => {
        try {
          const snap = await getDoc(userRef);
          if (snap.exists()) {
            const c = Number(snap.data()?.credits || 0);
            if (baseCreditsRef.current !== null && c !== baseCreditsRef.current) {
              baseCreditsRef.current = c;
              finishProcessing();
              return;
            }
          }
        } catch (e) {
          console.warn("soft fallback check error:", e);
        }
        // keep banner; webhook may still be running
      }, 8000);

      // Hard timeout after ~60s: clear spinner so UI never sticks forever
      timersRef.current.hard = window.setTimeout(() => {
        finishProcessing();
      }, 60000);
    }

    return () => {
      unsubUser();
      if (processedSubRef.current) {
        processedSubRef.current();
        processedSubRef.current = null;
      }
      if (timersRef.current.soft) window.clearTimeout(timersRef.current.soft);
      if (timersRef.current.hard) window.clearTimeout(timersRef.current.hard);
      timersRef.current.soft = null;
      timersRef.current.hard = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  return (
    <DashboardLayout>
      {/* Mobile-Only Back Button (below header) */}
      <div className="sm:hidden flex justify-start px-6 mt-4">
        <button
          onClick={() => navigate("/user-dashboard")}
          className="bg-[#1976d2] text-white text-sm font-bold font-[Montserrat] px-4 py-2 rounded-md"
        >
          ← Back
        </button>
      </div>

      {/* Title */}
      <h2 className="text-3xl font-bold text-center mt-8 mb-6">Your Wallet</h2>

      {/* Credit Display */}
      {loading ? (
        <div
          className="text-[1.15rem] font-[Montserrat] font-bold text-center"
          role="status"
          aria-live="polite"
        >
          <p>⏳ Adding credits...</p>
          <p className="text-sm font-normal mt-2">
            If credits are not updated in 1 minute, refresh the page.
          </p>
        </div>
      ) : (
        <p className="text-[1.15rem] font-[Montserrat] font-bold text-center">
          <strong>Current Credits:</strong> {credits}
        </p>
      )}

      {/* Buy Button */}
      <div className="flex justify-center mt-6">
        <button
          onClick={() => navigate("/shop")}
          style={{
            padding: "13.8px 27.6px",
            backgroundColor: "#1976d2",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            fontSize: "18.4px",
            fontFamily: "Montserrat",
            fontWeight: "bold",
            cursor: "pointer",
          }}
        >
          Buy More Credits
        </button>
      </div>
    </DashboardLayout>
  );
}

export default Wallet;
